import Service, { inject as service } from '@ember/service';
import { action, computed, get, set } from '@ember/object';
import { alias, notEmpty, reads } from '@ember/object/computed';
import { storageFor } from 'ember-local-storage';
import { dropTask, task } from 'ember-concurrency';
import { Promise, resolve } from 'rsvp';
import DS from 'ember-data';
import EventBusService, { ENVIRONMENT_RELOAD } from 'cio/services/event-bus';
import EnvironmentModel, {
  DeliverySettings,
  MetricDenominator,
  PageViewsResponse,
  SettingsEndpointResponse,
  SettingsEndpointResponseSettings,
} from 'cio/models/environment';
import { EMAIL_AND_ID_IDENTIFIER_CONFIG, IdentifierKey, Identifiers, IdentifierType } from 'cio/utils/identifiers';
import ApiService from './api';
import IdentityModel from 'cio/models/identity';
import DomainModel from 'cio/models/domain';
import Snippet from 'cio/models/snippet';
import { belongsToDomain } from 'cio/utils/group-identities-by-domain';
import { ActionType } from 'cio/models/action';
import { actionTypeName, DELIVERY_STATES_AND_ACTIONS_PER_TYPE } from 'cio/utils/action-info';
import CampaignModel, { RESTRICTED_ACTION_TYPES } from 'cio/models/campaign';
import { DEFAULT_MAX_COLLECTION_COUNT } from 'cio/utils/collections';
import ObjectTypeModel from 'cio/models/object-type';
import AuthService from 'cio/services/auth';
import FeaturesService from 'ember-feature-flags/services/features';
import AccountService from './account';
import { LogoSource, SubscriptionCenterSettings } from 'cio/utils/subscription-center';
import isDestroyed from 'cio/utils/is-destroyed';

export const PERMANENT_ACTIONS: string[] = [
  'email',
  'conditional_wait',
  'delay_seconds',
  'delay_time_window',
  'webhook',
  'create_event',
  'batch_update',
  'attribute_update',
  'static_seg_update',
  'time_zone_match',
  'conditional_branch',
  'multi_split_branch',
  'random_cohort_branch',
  'exit',
];

const externalActionsEnabledByDefault: string[] = ['twilio', 'slack'];

// mock type: not necessarily valid, necessary only to allow compilation
export interface MockEnvironmentModel {
  id?: string;
  fakePostSignup?: boolean;
  name: string;
  hexCode: string;
  hexCodeWithDefault: string;
  enabledActions: any[];
  identities: IdentityModel[];
  metricDenominator: MetricDenominator;
  sentAttributes: string[];
  sentEvents: string[];
  moreAttributes: boolean;
  moreEvents: boolean;
  identifierType: IdentifierType;
  identifiers: Identifiers;
  isEmailIdentified: boolean;
  isMultiIdentifier: boolean;
  optionalIdentifiers: IdentifierKey[];
  disabledIdentifiers: IdentifierKey[];
  enabledIdentifiers: IdentifierKey[];
  immutableIdentifiers: IdentifierKey[];
  allEnabledIdentifiers: IdentifierKey[];
  canIdentifyByEmail: boolean;
  canIdentifyById: boolean;
  emailOpenTracking: boolean;
  languageAttribute: string;
  hasLanguageAttribute: boolean;
  get: (key: string) => any;
  save: () => Promise<EnvironmentModel>;
  destroyRecord: () => Promise<unknown>;
  reload: () => Promise<EnvironmentModel | MockEnvironmentModel>;
  rollbackAttributes: () => unknown;
  hasDirtyAttributes: boolean;
  createdAt: number;
  getSettings: (this: EnvironmentModel | any, payload: Record<string, never>) => Promise<SettingsEndpointResponse>;
  updateSetting: (
    this: EnvironmentModel | any,
    payload: Partial<SettingsEndpointResponseSettings>,
  ) => Promise<SettingsEndpointResponse>;
  getSubscriptionCenter: (this: EnvironmentModel | any) => Promise<SubscriptionCenterSettings>;
  updateSubscriptionCenter: (
    this: EnvironmentModel | any,
    payload: SubscriptionCenterSettings,
  ) => Promise<SubscriptionCenterSettings>;
  pageViews: (this: EnvironmentModel | any, payload: { search: string }) => Promise<PageViewsResponse>;
  shard: string;
  subscriptionCenterEnabled: boolean;
  maxAllowedObjectTypes: number;
  deliverySettings: DeliverySettings;
  deliveryEmail: string;
  canUpdateDefaultTemplateEngine: boolean;
  defaultTemplateEngine: number;
}

// Stub values for integration tests. This is overwritten with an Ember Data model after the environment route loads.
export function mockEnvironmentModel(): MockEnvironmentModel {
  return {
    id: undefined,
    name: '',
    hexCode: '',
    hexCodeWithDefault: '',
    enabledActions: [],
    identities: [],
    metricDenominator: MetricDenominator.Delivered,
    sentAttributes: ['id', 'email', 'created_at', 'first_name', 'last_name'],
    sentEvents: ['login', 'purchase'],
    moreAttributes: false,
    moreEvents: false,
    identifierType: IdentifierType.MultiIdentifier,
    identifiers: EMAIL_AND_ID_IDENTIFIER_CONFIG,
    isEmailIdentified: false,
    isMultiIdentifier: true,
    optionalIdentifiers: [IdentifierKey.Id, IdentifierKey.Email],
    disabledIdentifiers: [],
    enabledIdentifiers: [IdentifierKey.Id, IdentifierKey.Email],
    immutableIdentifiers: [],
    allEnabledIdentifiers: [IdentifierKey.CioId, IdentifierKey.Id, IdentifierKey.Email],
    canIdentifyByEmail: true,
    canIdentifyById: true,
    emailOpenTracking: true,
    createdAt: new Date().getTime(),
    languageAttribute: '',
    hasLanguageAttribute: false,
    reload(): Promise<any> {
      return resolve(this);
    },
    rollbackAttributes() {},
    hasDirtyAttributes: false,
    get(key: string): any {
      return get(this as any, key);
    },
    save: () => resolve(mockEnvironmentModel() as unknown as EnvironmentModel),
    destroyRecord() {
      return Promise.resolve();
    },
    getSettings: (_: Record<string, never>) => {
      return Promise.resolve({
        settings: {
          customer_columns: [],
          object_type_columns: {},
          relationship_columns: {},
          metrics_timezone: 'Etc/GMT+4',
          journey_metrics_timezone: 'Etc/UTC',
          max_collection_size: DEFAULT_MAX_COLLECTION_COUNT,
        },
      });
    },
    updateSetting: (_: Record<string, never>) => {
      return Promise.resolve({
        settings: {
          customer_columns: [],
          object_type_columns: {},
          relationship_columns: {},
          metrics_timezone: 'Etc/GMT+4',
          journey_metrics_timezone: 'Etc/UTC',
          max_collection_size: DEFAULT_MAX_COLLECTION_COUNT,
        },
      });
    },
    getSubscriptionCenter: () => {
      return Promise.resolve({
        logoPath: null,
        logoSource: LogoSource.None,
        backgroundColor: null,
        foregroundColor: null,
      });
    },
    updateSubscriptionCenter: (payload: SubscriptionCenterSettings) => {
      return Promise.resolve(payload);
    },
    pageViews: (_: { search: string }) => {
      return Promise.resolve([]);
    },
    shard: 'shard1',
    subscriptionCenterEnabled: false,
    maxAllowedObjectTypes: 2,
    deliverySettings: DeliverySettings.Normal,
    deliveryEmail: '',
    canUpdateDefaultTemplateEngine: false,
    defaultTemplateEngine: 1,
  };
}

export default class EnvironmentService extends Service {
  @service api!: ApiService;
  @service auth!: AuthService;
  @service eventBus!: EventBusService;
  @service store!: DS.Store;
  @service features!: FeaturesService;
  @service account!: AccountService;

  localStorage = storageFor('cio-settings');

  current: EnvironmentModel | MockEnvironmentModel = mockEnvironmentModel();

  domains!: DomainModel[] | null;
  snippets!: Snippet[] | null;
  objectTypes!: ObjectTypeModel[] | null;

  @alias('localStorage.environment') local!: string;

  @reads('current.sentAttributes') sentAttributes!: string[];
  @reads('current.sentEvents') sentEvents!: string[];
  @reads('current.moreAttributes') moreAttributes!: boolean;
  @reads('current.moreEvents') moreEvents!: boolean;
  @reads('current.pushProviders') pushProviders!: string[];
  @reads('current.enabledActions') enabledActions!: string[];
  @notEmpty('current.verifiedIdentities') hasVerifiedSenderIdentity!: boolean;
  @reads('current.deviceAttributes') deviceAttributes!: string[];

  @computed('enabledActions.[]')
  get availableMeasurableActionTypes() {
    return Object.entries(DELIVERY_STATES_AND_ACTIONS_PER_TYPE)
      .filter(([type, measures]) => measures.length > 0 && this.enabledAction(type))
      .map(([type]) => actionTypeName(type as ActionType));
  }

  enabledAction(action: string) {
    // during a transition, the current environment may be destroyed and we may be in the process of
    // switching to a new one (which is not yet loaded)
    if (isDestroyed(this.current)) {
      return false;
    }
    action = (action || '').replace(/_action$/, '');
    if (action === 'collection_query') {
      return this.account.planFeatures.collections.enabled;
    }
    let enabledActions = [...this.enabledActions, ...externalActionsEnabledByDefault];
    return PERMANENT_ACTIONS.includes(action) || enabledActions?.includes(action);
  }

  domainForIdentity(identity: IdentityModel) {
    return (this.domains ?? []).find((domain) => belongsToDomain(domain, identity));
  }

  @action
  availableChannelsForCampaign(campaign: CampaignModel) {
    let restrictedCampaignActionTypes = RESTRICTED_ACTION_TYPES[campaign.type] ?? [];

    return this.availableMeasurableActionTypes.filter((typeName) => {
      return !restrictedCampaignActionTypes.includes(`${typeName}_action` as ActionType);
    });
  }

  reload = dropTask(async ({ isBackgroundTask }: { isBackgroundTask?: boolean } = {}) => {
    try {
      await this.current.reload({ adapterOptions: { isBackgroundTask } });
      this.eventBus.publish(ENVIRONMENT_RELOAD);
    } catch (e) {
      // Ignore so we can continue to check next time
    }
  });

  loadSnippets = task(async () => {
    try {
      let snippets: Snippet[] = [];

      let page = 1;
      let perPage = 100;
      let pagedSnippets: any;
      do {
        pagedSnippets = await this.store.query('snippet', { page, limit: perPage });
        snippets = snippets.concat(pagedSnippets);
        page++;
      } while (pagedSnippets.length !== 0 && pagedSnippets.length === perPage);

      set(this, 'snippets', snippets);
    } catch (e) {
      // Ignore
    }
  });

  loadDomains = task(async () => {
    try {
      let domains = await this.store.findAll('domain');
      this.set('domains', domains.toArray());
    } catch {
      // Ignore
    }
  });

  @computed('local', 'current', 'auth.user.{lastEnvironment,defaultEnvironment}')
  get lastEnvironmentId(): string | undefined | null {
    // Objects may be destroyed when switching environments and re-seeding data
    if (isDestroyed(this) || isDestroyed(this.auth) || this.auth.user == null || isDestroyed(this.auth.user)) {
      return null;
    }

    let lastEnvId = this.auth.user?.belongsTo('lastEnvironment').id();

    if (this.current.id != null) {
      return this.current.id;
    }

    if (this.local != null) {
      return this.local;
    }

    if (lastEnvId != null && this.auth.user?.canAccessEnvironment(lastEnvId)) {
      return lastEnvId;
    }

    if (this.auth.user?.defaultEnvironment?.id != null) {
      return this.auth.user?.defaultEnvironment?.id;
    }

    return null;
  }
}

declare module '@ember/service' {
  interface Registry {
    environment: EnvironmentService;
  }
}
